import React, { useState } from 'react';
import { formatUnixToPakistanTime } from '../utils/dateUtils';
import { useBidding } from '../hooks/useBidding';
import bidService from '../services/bidService';
import ProposalModal from './ProposalModal';
import { useFreelancerAPI } from '../hooks/useFreelancerAPI';
import { useUsersStore } from '../store/useUsersStore';
import axios from 'axios';
import { getAuthHeaders } from '../utils/api';
import { STORAGE_KEYS } from '../utils/apiUtils';


const ProjectCard = ({ project, usersMap = null, projectIdsBidOn = null, onBidPlaced = null, hasBidOn = null }) => {
  const { calculateBidAmount, placeBidManual } = useFreelancerAPI();
  const { loading, error, success, clearError } = useBidding();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showFullDesc, setShowFullDesc] = useState(false);
  const [initialProposal, setInitialProposal] = useState(null);
  const [apiProposalLoading, setApiProposalLoading] = useState(false);

  // get selected sub-user (to send templates/skills/bidder name)
  const selectedSubUser = useUsersStore((s) => s.getSelectedUser && s.getSelectedUser());

  // Extract project data with fallbacks based on the actual API response structure
  const {
    id = 'N/A',
    title = 'Untitled Project',
    status = 'N/A',
    seo_url = null,
    currency = {},
    description,
    preview_description = null,
    submitdate = null,
    type = 'N/A',
    bidperiod = 'N/A',
    budget,
    bid_stats = {},
    users,
  } = project;

  // Use preview_description if description is null, or fallback to default
  const projectDescription = description || preview_description || 'No description available';
  const DESCRIPTION_TRUNCATE_LEN = 100;
  const isLongDescription = projectDescription && projectDescription.length > DESCRIPTION_TRUNCATE_LEN;
  const truncatedDescription = isLongDescription
    ? `${projectDescription.substring(0, DESCRIPTION_TRUNCATE_LEN)}...`
    : projectDescription;

  const getOwnerCountry = (proj) => {
    if (!proj) return null;
    // Prefer direct owner_info.country.name (new API shape)
    const ownerInfoCountry = proj.owner_info?.country?.name;
    if (ownerInfoCountry) return String(ownerInfoCountry).trim();

    const ownerId = proj.owner_id ?? proj.owner?.id ?? proj.user_id ?? null;

    // prefer usersMap prop from parent, otherwise use project.users or common fallback globals
    const mapCandidates = [
      usersMap,
      proj.users,
      proj._users,
      proj.users_map,
      // sometimes you may attach a global users object; try it if present
      typeof window !== 'undefined' ? window.__SF_USERS : undefined,
      {}
    ];

    let resolvedOwner = null;
    for (const map of mapCandidates) {
      if (!map || typeof map !== 'object') continue;
      // direct key match
      resolvedOwner = map[ownerId] || map[String(ownerId)] || map[Number(ownerId)];
      if (resolvedOwner) break;
      // try scanning entries if keys are different
      const keys = Object.keys(map || {});
      if (keys.length > 0 && !resolvedOwner) {
        for (const k of keys) {
          const u = map[k];
          if (u && (u.id === ownerId || String(u.id) === String(ownerId))) {
            resolvedOwner = u;
            break;
          }
        }
      }
    }

    // final fallbacks
    resolvedOwner = resolvedOwner || proj.owner || proj.user || (proj.users && proj.users[Object.keys(proj.users)[0]]) || null;

    const country =
      resolvedOwner?.location?.country?.name ||
      resolvedOwner?.profile?.location?.country?.name ||
      proj.location?.country?.name ||
      null;

    return country ? String(country).trim() : null;
  };

  const getOwnerUserObject = (proj) => {
    if (!proj) return null;
    const ownerId = proj.owner_id ?? proj.owner?.id ?? proj.user_id ?? null;
    const mapCandidates = [
      usersMap,
      proj.users,
      proj._users,
      proj.users_map,
      typeof window !== 'undefined' ? window.__SF_USERS : undefined,
      {}
    ];
    let resolvedOwner = null;
    for (const map of mapCandidates) {
      if (!map || typeof map !== 'object') continue;
      resolvedOwner = map[ownerId] || map[String(ownerId)] || map[Number(ownerId)];
      if (resolvedOwner) break;
      for (const k of Object.keys(map || {})) {
        const u = map[k];
        if (u && (u.id === ownerId || String(u.id) === String(ownerId))) {
          resolvedOwner = u;
          break;
        }
      }
      if (resolvedOwner) break;
    }
    return resolvedOwner || proj.owner || proj.user || (proj.users && proj.users[Object.keys(proj.users)[0]]) || null;
  };

  const ownerCountry = getOwnerCountry(project);
  const ownerUser = getOwnerUserObject(project);
  const ownerUsername = ownerUser?.username || ownerUser?.user_name || ownerUser?.displayName || null;
  const ownerProfileUrl = ownerUsername ? `https://www.freelancer.com/u/${ownerUsername}` : null;

  const clientPublicName = ownerUser?.public_name ?? ownerUser?.publicName ?? ownerUser?.displayName ?? ownerUsername ?? null;



  // Format currency information
  const currencyCode = currency?.code || 'USD';
  const currencySign = currency?.sign || '$';
  const currencyName = currency?.name || 'N/A';

  // Format budget
  const budgetMin = budget?.minimum || 0;
  const budgetMax = budget?.maximum || 0;

  const budgetDisplay = budgetMin && budgetMax && budgetMax > budgetMin
    ? `${currencySign}${budgetMin} - ${currencySign}${budgetMax}`
    : budgetMin || budgetMax
      ? `${currencySign}${budgetMin || budgetMax}`
      : 'N/A';

  // parse required skills from project response (jobs array may contain {id, name})
  const requiredSkillsRaw = project.jobs || project.jobs_details || project.skills || [];
  const requiredSkills = Array.isArray(requiredSkillsRaw)
    ? requiredSkillsRaw.map((j) => (typeof j === 'string' ? j : j?.name || j?.title || '')).filter(Boolean)
    : [];



  // Format submit date
  const formattedDate = submitdate ? formatUnixToPakistanTime(submitdate) : 'N/A';

  // Get bid count
  const bidCount = bid_stats?.bid_count || 0;
  const bidAvg = bid_stats?.bid_avg || 0;
  const bidAvgRounded = (typeof bidAvg === 'number' && !Number.isNaN(bidAvg)) ? Math.round(bidAvg) : null;
  const bidAvgDisplay = bidAvgRounded !== null ? `${currencySign}${bidAvgRounded}` : null;

  const isPaymentVerified = users?.[project.owner_info]?.status?.payment_verified || `N/A`;

  // Already-bid check: Firestore (hasBidOn) first, then projectIdsBidOn array, then localStorage
  const projectIdForBid = id !== 'N/A' ? id : (project.projectId ?? project.project_id);
  const hasAlreadyBid =
    (typeof hasBidOn === 'function' && projectIdForBid != null && hasBidOn(projectIdForBid)) ||
    (Array.isArray(projectIdsBidOn) && projectIdsBidOn.includes(String(projectIdForBid))) ||
    bidService.hasBidOnProject(projectIdForBid);

  const alreadyBidMessage = 'You have already placed a bid on this project.';


  const getClientReview = (proj) => {
    if (!proj) return null;

    // Prefer reputation provided directly on project.owner_info (new API shape)
    const ownerInfoRep = proj.owner_info?.reputation;
    if (ownerInfoRep) {
      const v = ownerInfoRep.entire_history?.overall ?? ownerInfoRep.last12months?.overall ?? ownerInfoRep.last3months?.overall;
      if (typeof v === 'number' && !Number.isNaN(v)) return v > 0 ? v : null;
      return null;
    }

    // Fallback: resolve owner object from maps or project fields
    const ownerId = proj.owner_id ?? proj.owner?.id ?? proj.user_id ?? null;
    const candidateMaps = [usersMap, proj.users, proj._users, proj.users_map];
    let owner = null;
    for (const map of candidateMaps) {
      if (!map || typeof map !== 'object') continue;
      owner = map[ownerId] || map[String(ownerId)] || map[Number(ownerId)];
      if (owner) break;
      for (const k of Object.keys(map || {})) {
        const u = map[k];
        if (u && (u.id === ownerId || String(u.id) === String(ownerId))) {
          owner = u;
          break;
        }
      }
      if (owner) break;
    }
    owner = owner || proj.owner || proj.user || (proj.users && proj.users[Object.keys(proj.users)[0]]) || null;

    const overallPaths = [
      owner?.employer_reputation?.entire_history?.overall,
      owner?.employer_reputation?.last3months?.overall,
      owner?.employer_reputation?.last12months?.overall,
      owner?.profile?.reputation?.entire_history?.overall,
      owner?.profile?.reputation?.last3months?.overall,
      owner?.profile?.reputation?.last12months?.overall,
    ];

    for (const v of overallPaths) {
      if (typeof v === 'number' && !Number.isNaN(v)) {
        if (v > 0) return v;
        return null;
      }
    }

    return null;
  };

  const clientReview = getClientReview(project);

  // Prefer reputation from project.owner_info when available
  const employerReputation = project.owner_info?.reputation || ownerUser?.employer_reputation || ownerUser?.profile?.employer_reputation || project.employer_reputation || {};
  const repEntire = employerReputation?.entire_history || {};
  const rep3 = employerReputation?.last3months || {};
  const rep12 = employerReputation?.last12months || {};

  const formatCount = (v) => (typeof v === 'number' ? v : 0);
  const formatRating = (v) => (typeof v === 'number' ? v.toFixed(1) : 'N/A');


  const handleOpenBid = async () => {
    if (hasAlreadyBid) {
      alert(alreadyBidMessage);
      return;
    }

    setIsModalOpen(true);
    setApiProposalLoading(true);
    setInitialProposal(null);
    try {
      const payload = {
        proposal: Array.isArray(selectedSubUser?.templates) ? selectedSubUser.templates : [],
        bidder_name: selectedSubUser?.sub_username || selectedSubUser?.name || undefined,
        client_name: clientPublicName || ownerUsername || 'Client',
        job_title: title,
        job_description: projectDescription,
        skills: Array.isArray(selectedSubUser?.skills) ? selectedSubUser.skills : [],
      };
      const url = `${process.env.REACT_APP_API_BASE_URL.replace(/\/$/, '')}/recommend-proposal`;
      const headers = getAuthHeaders() || {};


      const resp = await axios.post(url, payload, {
        headers,
        // withCredentials: true, // ensure cookies (access_token) are sent
        timeout: 30000,
      });
      if (resp?.status === 200 && resp.data) {
        // const proposalString = (typeof rawProposal === 'string' && rawProposal.trim().length > 0) ? rawProposal : null;
        // setInitialProposal(proposalString);
        const rawProposal = resp.data.proposal ?? (resp.data.data && resp.data.data.proposal) ?? null;
        const apiProposal = (typeof rawProposal === 'string' && rawProposal.trim().length > 0) ? rawProposal : null;
        // fallback to sub-user's saved general proposal when API returns empty string
        const subUserGeneral =
          selectedSubUser?.general_proposal ||
          selectedSubUser?.generalProposal ||
          selectedSubUser?.generalProposalText ||
          selectedSubUser?.ai_templates ||
          null;
        setInitialProposal(apiProposal || (typeof subUserGeneral === 'string' && subUserGeneral.trim().length > 0 ? subUserGeneral : null));

      } else {
        // no proposal returned — keep null and modal will fallback to local proposal generation
        console.warn('recommend-proposal returned unexpected response', resp?.status, resp?.data);
        setInitialProposal(null);
      }
    } catch (err) {
      console.error('Error fetching recommended proposal:', err);
      setInitialProposal(null);
    } finally {
      setApiProposalLoading(false);
      // setIsModalOpen(true);
    }
  };


  // Handle bid submission from modal
  const handleSubmitBid = async ({ amount, period, description }) => {
    try {
      const projectMeta = {
        seo_url,
        type,
        title,
        description: projectDescription,
        budget,
        currency,
      };

      const result = await placeBidManual({
        projectId: projectIdForBid,
        amount,
        period,
        description,
        projectMeta,
      });

      if (result?.success) {
        if (typeof onBidPlaced === 'function') onBidPlaced(projectIdForBid);
        try {
          const prev = bidService.getLocalBids();
          prev.push({
            projectId: projectIdForBid,
            bidderId: selectedSubUser?.user_bid_id ?? selectedSubUser?.bidder_id,
            ts: Date.now(),
          });
          localStorage.setItem(STORAGE_KEYS.BIDS, JSON.stringify(prev));
        } catch (e) {
          // ignore localStorage errors
        }
        console.log('Bid response:', result.data);
        alert('Bid placed successfully!');
        return result; // return structured result { success, data, skipHistory }
      } else {
        const msg = result?.message || 'Failed to place bid';
        console.error('Bid error:', msg);
        alert(`Error: ${msg}`);
        return result;
      }
    } catch (err) {
      console.error('Error in handleSubmitBid:', err);
      alert(`Error: ${err?.message || 'An unexpected error occurred'}`);
      return { success: false, message: err?.message || 'An unexpected error occurred' };
    }
  };
  const handleViewProject = () => {
    if (seo_url) {
      const projectUrl = `https://www.freelancer.com/projects/${seo_url}`;
      window.open(projectUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="card p-6 h-full flex flex-col border-2 border-light-blue">
      {/* Header */}
      <div className="flex-1">
        {/* Project ID and Status */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs bg-light-blue text-blue-800 px-2 py-1 rounded-full font-medium">
            ID: {id}
          </span>
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${status === 'active'
            ? 'bg-green-100 text-green-800'
            : 'bg-gray-100 text-gray-800'
            }`}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
        </div>

        <h3 className="text-lg font-semibold text-gray-900 mb-3 line-clamp-2 leading-tight">
          {title}
        </h3>


        {/* Description */}
        <p className="text-gray-600 text-sm mb-2 leading-relaxed">
          {showFullDesc ? projectDescription : truncatedDescription}
        </p>
        {isLongDescription && (
          <button
            onClick={() => setShowFullDesc((s) => !s)}
            className="text-xs text-blue-600 hover:underline mb-4"
            aria-expanded={showFullDesc}
          >
            {showFullDesc ? 'Show less' : 'Read more...'}
          </button>
        )}
         {/* Required Skills Tags */}
        {requiredSkills && requiredSkills.length > 0 && (
          <div className="mb-4">
            <div className="text-xs text-gray-500 mb-2">Required skills</div>
            <div className="flex flex-wrap gap-2">
              {requiredSkills.slice(0, 8).map((s, i) => (
                <span key={i} className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded-full">
                  {s}
                </span>
              ))}
              {requiredSkills.length > 8 && (
                <span className="text-xs text-gray-500 self-center">+{requiredSkills.length - 8} more</span>
              )}
            </div>
          </div>
        )}

        {/* Project Details Grid */}
        <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-primary-black-shade-10 opacity-40 mb-8">

          {/* Type */}
          <div className="flex items-center gap-1">
            <span className="text-sm">
              Type: {type || 'N/A'}
            </span>
          </div>

          {/* Bid Period */}
          <div className="flex items-center gap-1">
            <span className="text-sm">
              Bid Period: {bidperiod !== 'N/A' ? `${bidperiod} days` : 'N/A'}
            </span>
          </div>

          {/* Client Country */}
          <div className="flex items-center gap-1">
            <span className="text-sm">
              Client Country: {ownerCountry || 'N/A'}
            </span>
          </div>

          {/* Client Username */}
          {ownerUsername && (
            <div className="flex items-center gap-1">
              <span className="text-sm">Client:</span>
              <a
                href={ownerProfileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm  font-medium text-blue-600 hover:text-blue-800"
              >
                @{ownerUsername}
              </a>
              {/* Public name if available */}
              {(ownerUser?.public_name || ownerUser?.publicName) && (
                <span className="text-sm text-gray-500">
                  ({ownerUser.public_name ?? ownerUser.publicName})
                </span>
              )}
            </div>
          )}

        </div>
        {/* Currency Information */}
        <div className={`p-3 rounded-lg mb-4 ${currencyCode === 'USD'
          ? 'bg-green-50 border border-green-200'
          : 'bg-yellow-50 border border-yellow-200'
          }`}>
          <div className="flex items-center justify-between text-xs">
            <span className={`font-medium ${currencyCode === 'USD' ? 'text-green-600' : 'text-yellow-600'
              }`}>Currency</span>
            <span className={`${currencyCode === 'USD' ? 'text-green-800' : 'text-yellow-800'
              }`}>
              {currencyCode} ({currencySign}) - {currencyName}
            </span>
          </div>

          {/* Payment Verified */}
          <span className="text-gray-500 block text-xs mt-2">Payment Verified</span>
          <span className="font-medium text-gray-900 text-sm">
            {isPaymentVerified === true ? 'Yes' : isPaymentVerified === false ? 'No' : 'N/A'}
          </span>
        </div>
        {/* Client Review */}
        <div className="bg-gray-50 p-2 rounded-lg mb-4">
          <span className="text-gray-500 block text-xs">Client Review</span>
          <span className="font-medium text-gray-900 text-sm">
            {typeof clientReview === 'number' ? `${clientReview.toFixed(1)} / 5` : 'No reviews yet'}
          </span>
        </div>
        <div className="bg-white p-3 rounded-lg mb-4 ">
          <div className="flex items-center justify-between mb-3">
            <div className="font-semibold">Client History</div>
            <div className="text-xs text-gray-500">Source: employer_reputation</div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs bg-light-blue-shade-20 p-2 rounded-lg">
            {[
              { key: 'Entire', data: repEntire },
              { key: 'Last 3m', data: rep3 },
              { key: 'Last 12m', data: rep12 },
            ].map(({ key, data }) => (
              <div key={key} className="p-2 bg-white rounded">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold">{key}</div>
                  <div className="text-sm text-yellow-600 flex items-center gap-1">
                    {/* star icon */}
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.966a1 1 0 00.95.69h4.173c.969 0 1.371 1.24.588 1.81l-3.378 2.455a1 1 0 00-.364 1.118l1.287 3.966c.3.921-.755 1.688-1.54 1.118L10 15.347l-3.351 2.603c-.785.57-1.84-.197-1.54-1.118l1.287-3.966a1 1 0 00-.364-1.118L2.653 9.393c-.783-.57-.38-1.81.588-1.81h4.173a1 1 0 00.95-.69L9.05 2.927z" />
                    </svg>
                    <span className="font-medium">{formatRating(data?.overall)}</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-2 text-xs text-gray-600">
                  <div className="flex items-center gap-2">
                    {/* reviews icon */}
                    <div className='flex gap-4'>
                      <div className="text-xs w-16">Reviews</div>
                      <div className="font-medium">{formatCount(data?.reviews ?? data?.all ?? data?.complete)}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* complete icon */}
                    {/* <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M16.707 5.293a1 1 0 010 1.414L8.414 15 5 11.586a1 1 0 10-1.414 1.414l4 4a1 1 0 001.414 0l9-9a1 1 0 00-1.414-1.414z" />
                    </svg> */}
                    <div className='flex gap-4'>
                      <div className="text-xs w-16">Complete</div>
                      <div className="font-medium w-8">{formatCount(data?.complete)}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* incomplete icon */}
                    <div className='flex gap-4'>
                      <div className="text-xs w-16">Incomplete</div>
                      <div className="font-medium ">{formatCount(data?.incomplete)}</div>
                    </div>
                  </div>


                  {/* completion rate icon */}
                  {/* <svg className="w-4 h-4 text-indigo-500" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11H9v5h2V7z" />
                    </svg>
                    <div>
                      <div className="text-xs">Completion</div>
                      <div className="font-medium">{(typeof data?.completion_rate === 'number') ? `${(data.completion_rate * 100).toFixed(0)}%` : 'N/A'}</div>
                    </div> */}

                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-auto space-y-3">
        {/* Budget */}
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 uppercase tracking-wide">Budget</span>
          <span className="text-lg font-bold text-green-600">{budgetDisplay}</span>
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
              <span className="text-gray-600">{bidCount} bids</span>
              <span className="text-gray-600">{bidAvgDisplay ? `avg ${bidAvgDisplay}` : 'avg N/A'}</span>
            </div>
          </div>

          <div className="text-xs text-gray-500">
            {formattedDate}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-red-600">{error}</p>
                <button
                  onClick={clearError}
                  className="text-red-700 hover:text-red-800 text-xs font-medium mt-1 underline"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-green-600 font-medium">Bid placed successfully!</p>
            </div>
          </div>
        )}

        {/* Action row: already-bid message (when applicable) and buttons */}
        <div className="pt-3 border-t border-gray-100 px-14 mx-auto">
          {hasAlreadyBid && (
            <p className="text-xs text-gray-600 mb-2" title={alreadyBidMessage}>
              {alreadyBidMessage}
            </p>
          )}
          <div className="flex items-center justify-between space-x-2">
          {/* View Project Button */}
          <button
            onClick={handleViewProject}
            disabled={!seo_url}
            className={`
              text-xs py-2.5 px-3 rounded-lg font-medium transition-all duration-200 flex justify-center items-center space-x-1 flex-1
              ${!seo_url
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-primary-blue hover:bg-blue-500 text-white shadow-sm hover:shadow-md transform hover:-translate-y-0.5'
              }
            `}
          >
            <span>{seo_url ? 'View Project' : 'No Link'}</span>
          </button>

          {/* Place Bid – disabled when hasAlreadyBid (Firestore listener keeps this in sync for auto-bids) */}
          <button
            onClick={handleOpenBid}
            disabled={loading || hasAlreadyBid}
            title={hasAlreadyBid ? alreadyBidMessage : undefined}
            className={`
              text-xs py-2.5 px-3 rounded-lg font-medium transition-all duration-200 flex justify-center items-center space-x-1 flex-1
              ${hasAlreadyBid
                ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                : loading
                  ? 'bg-blue-100 text-blue-600 cursor-not-allowed'
                  : 'bg-white border border-primary-blue hover:bg-primary-blue text-primary-blue hover:text-white shadow-sm hover:shadow-md transform hover:-translate-y-0.5'
              }
            `}
          >
            {loading ? (
              <>
                <div className="w-3 h-3 border border-blue-600 border-t-transparent rounded-full animate-spin items-center flex justify-center"></div>
                <span>Placing...</span>
              </>
            ) : hasAlreadyBid ? (
              <span>Bid Placed</span>
            ) : (
              <>
                <span>Place Bid</span>
              </>
            )}
          </button>
          </div>
        </div>
      </div>

      {/* Proposal Modal */}
      <ProposalModal
        open={isModalOpen}
        url={seo_url}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmitBid}
        projectId={project.id}
        projectTitle={project.title}
        projectDescription={project.description}
        budget={project.budget}
        type={project.type}
        calculateBidAmount={calculateBidAmount}
        clientPublicName={ownerUser?.public_name ?? ownerUser?.publicName ?? null}
        initialProposal={initialProposal}
        apiLoading={apiProposalLoading}
        currency={project.currency}
      />
    </div>
  );
};

export default ProjectCard;